const PC_MEDIA_QUERY = "(min-width: 1025px)";
const PIN_TOP_BASE = 1030;
const DESIGN_WIDTH = 1920;
const BOTTOM_OFFSET = 32; // fixed/bottom時の画面下からの距離(DESIGN_WIDTH幅時)
const SCROLL_BOTTOM_OFFSET = 45; // c-scroll__contのbottom位置(DESIGN_WIDTH幅時)
const TALL_VH_MAX = 1323;

const TABLET_MEDIA_QUERY = "(min-width: 768px) and (max-width: 1024px)";
const TABLET_DESIGN_WIDTH = 768;
const TABLET_PIN_TOP_BASE = 1000; // iPad帯のnews定位置(TABLET_DESIGN_WIDTH幅時)
const TABLET_BOTTOM_OFFSET = 16; // iPad帯fixed時の画面下からの距離(TABLET_DESIGN_WIDTH幅時)
const TABLET_BOTTOM_VH_MIN = 1000; // この高さ以上はCSSのheightメディアクエリがbottom配置するためJSは関与しない

// newsの配置モードをjs-mainのdata-news-positionで切替する(スクロール追従なし)
// PC(1025px〜):
//   pinned(既定): top1030px(fluid)に固定
//   bottom      : 画面下32px(fluid)相当に配置し、mainvisualを1画面分に拡張してpvとの被りを防ぐ
//   fixed       : 低い窓で画面下32px(fluid)に追従し、top1030pxに達したらpinnedへ
// iPad帯(768〜1024px):
//   高さ1000px未満のみfixed(画面下16px追従)→pinned(top1000相当で停止)をPCと同じ仕組みで行う
export default class News {
    constructor() {
        this.newsInner = document.querySelector(".js-news-inner");
        this.scrollEl = document.querySelector(".js-scroll");
        this.mediaQuery = window.matchMedia(PC_MEDIA_QUERY);
        this.tabletMediaQuery = window.matchMedia(TABLET_MEDIA_QUERY);
        this.currentPosition = "";
        this.currentScroll = "";
        this.currentScrollHide = false;
        this.rafId = null;

        if (!this.newsInner) return;

        this.handleScroll = this.requestUpdate.bind(this);
        this.handleResize = this.requestUpdate.bind(this);
        this.handleMediaChange = this.update.bind(this);

        this.init();
    }

    init() {
        this.update();

        window.addEventListener("scroll", this.handleScroll, { passive: true });
        window.addEventListener("resize", this.handleResize);
        this.mediaQuery.addEventListener("change", this.handleMediaChange);
        this.tabletMediaQuery.addEventListener("change", this.handleMediaChange);
        window.addEventListener("load", this.handleResize);
    }

    requestUpdate() {
        if (this.rafId) return;

        this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            this.update();
        });
    }

    update() {
        if (this.mediaQuery.matches) {
            this.updatePc();
            return;
        }

        if (this.tabletMediaQuery.matches) {
            this.updateTablet();
            return;
        }

        this.setPosition("");
        this.setScroll("");
        this.setScrollHide(false);
    }

    updatePc() {
        // scroll誘導(bottom:45)の下端がnewsのベスト位置(top:1030)に達したらbottomへ切替。
        // bottom時はnewsがright:168に逃げるためscrollと同じ高さでも横並びで共存できる
        const isTall = window.innerHeight > this.getFluid(TALL_VH_MAX);
        const isBottom = window.innerHeight - this.getFluid(SCROLL_BOTTOM_OFFSET) >= this.getFluid(PIN_TOP_BASE) && !isTall;

        let position = "pinned";
        if (isBottom) {
            position = "bottom";
        } else if (!isTall) {
            // 低い窓はfixed(bottom:32px)で追従し、上端がtop:1030pxに達したらpinnedで停止
            const fixedTop = window.scrollY + window.innerHeight - this.getFluid(BOTTOM_OFFSET) - this.newsInner.offsetHeight;
            if (fixedTop < this.getFluid(PIN_TOP_BASE)) position = "fixed";
        }

        this.setPosition(position);
        // data-scrollは窓サイズのみに連動(newsの横位置が紐づくためスクロールでは変えない)
        this.setScroll(isTall ? "inactive" : "active");
        // スクロールを始めたら矢印を-is-hideで非表示、先頭に戻れば再表示
        this.setScrollHide(window.scrollY > 20);
    }

    // iPad帯(768〜1024px)。低い画面のみfixed(画面下追従)→pinned(top1000相当で停止)を行う。
    // 高さ1000px以上のbottom配置はCSSのheightメディアクエリ側で完結しているためJSは属性を外す
    updateTablet() {
        this.setScroll("");
        this.setScrollHide(false);

        if (window.innerHeight >= TABLET_BOTTOM_VH_MIN) {
            this.setPosition("");
            return;
        }

        const fixedTop = window.scrollY + window.innerHeight - this.getTabletVw(TABLET_BOTTOM_OFFSET) - this.newsInner.offsetHeight;
        this.setPosition(fixedTop < this.getTabletVw(TABLET_PIN_TOP_BASE) ? "fixed" : "pinned");
    }

    // SCSSのgetFluid()と同値(1920px幅基準で縮小、1920px以上はpx固定)を返す
    getFluid(size) {
        return Math.min((window.innerWidth / DESIGN_WIDTH) * size, size);
    }

    // SCSSのgetVW(size, 768)と同値(768px幅基準のvw換算)を返す
    getTabletVw(size) {
        return (window.innerWidth / TABLET_DESIGN_WIDTH) * size;
    }

    // mainvisual側もCSSから参照するためjs-mainに付与する
    setPosition(position) {
        if (this.currentPosition === position) return;

        this.currentPosition = position;
        const main = document.querySelector(".js-main");

        if (position) {
            main.setAttribute("data-news-position", position);
        } else {
            main.removeAttribute("data-news-position");
        }
    }

    // スクロール中の矢印非表示はjs-scrollへの-is-hideで行う(data-scrollとは独立)
    setScrollHide(isHide) {
        if (this.currentScrollHide === isHide || !this.scrollEl) return;

        this.currentScrollHide = isHide;
        this.scrollEl.classList.toggle("-is-hide", isHide);
    }

    // スクロール誘導(c-scroll)の表示状態もjs-mainに付与する
    setScroll(scroll) {
        if (this.currentScroll === scroll) return;

        this.currentScroll = scroll;
        const main = document.querySelector(".js-main");

        if (scroll) {
            main.setAttribute("data-scroll", scroll);
        } else {
            main.removeAttribute("data-scroll");
        }
    }
}
